/**
 * Pathway publish flow (Sprint 4.3.4, PROD-653).
 *
 * "Publish version" CTA + demo-mode preview. Serialises the active
 * pathway to the 16-folder JSON file map, diffs it against the
 * caller-provided `lastPublishedFiles`, surfaces the diff inline, and
 * emits a verb-shape FHIR `AuditEvent` with
 * `source = 'pathway-publish-version'` (HC9 strict registry).
 *
 * Phase D placeholder: live mode would post the diff to a backend git
 * worker; this prototype emits the audit event only and surfaces the
 * diff as a "simulated PR" preview in the UI.
 *
 * The publish gate (HC8 fail-closed) refuses publish when:
 *   - Any open draft exists for the active pathway.
 *   - Sara-test passes < 28/28.
 *   - A cited component has been deleted.
 *
 * HC5: zero em / en dashes.
 * HC6 a11y: gate failures render in a `role="alert"` region; the CTA is
 * a real `<button>` with a labelled disabled state when gated.
 */
import { useMemo, useState } from 'react';
import {
  bundleToFileMap,
  diffFileMaps,
  formatDiffSummary,
  type BundleDiffEntry,
} from '../../services/care-flow-components/serializer';
import {
  evaluatePublishGate,
  type PublishGateResult,
} from '../../services/care-flow-components/publish-gate';
import type { CareFlowComponentBundle } from '../../services/care-flow-components/types';
import {
  auditPayloadToFhir,
  type VerbAuditPayload,
} from '../../services/fhir-mappers/audit-event';
import type { AuditEvent } from '../../services/fhir/types';

export const PUBLISH_AUDIT_SOURCE = 'pathway-publish-version';

export interface PathwayPublishFlowProps {
  pathwayId: string;
  tenantId: string;
  pathwayLabel: string;
  bundle: CareFlowComponentBundle;
  lastPublishedFiles?: Record<string, string>;
  openDraftCount: number;
  saraPassCount: number;
  saraTotalCount: number;
  citedComponentIds: ReadonlyArray<string>;
  subjectReference: string;
  onPublish?: (event: AuditEvent, diff: ReadonlyArray<BundleDiffEntry>) => void;
  className?: string;
}

export function PathwayPublishFlow({
  pathwayId,
  tenantId,
  pathwayLabel,
  bundle,
  lastPublishedFiles = {},
  openDraftCount,
  saraPassCount,
  saraTotalCount,
  citedComponentIds,
  subjectReference,
  onPublish,
  className,
}: PathwayPublishFlowProps) {
  const [lastPublished, setLastPublished] = useState<{
    event: AuditEvent;
    diff: ReadonlyArray<BundleDiffEntry>;
  } | null>(null);

  const gate: PublishGateResult = useMemo(
    () =>
      evaluatePublishGate({
        openDraftCount,
        saraPassCount,
        saraTotalCount,
        bundle,
        citedComponentIds,
      }),
    [openDraftCount, saraPassCount, saraTotalCount, bundle, citedComponentIds],
  );

  const fileMap = useMemo(() => bundleToFileMap(bundle), [bundle]);
  const diff = useMemo(
    () => diffFileMaps(lastPublishedFiles, fileMap),
    [lastPublishedFiles, fileMap],
  );

  const handlePublish = () => {
    if (!gate.ok) return;
    const payload: VerbAuditPayload = {
      recorded: new Date(0).toISOString(),
      source: PUBLISH_AUDIT_SOURCE,
      tenantId,
      subject: {
        reference: subjectReference,
        display: 'Pathway author',
      },
      verb: 'published',
      verbDisplay: 'Published care-flow component bundle',
      object: {
        reference: `urn:cps:pathway:${pathwayId}`,
        display: pathwayLabel,
        typeCode: 'pathway-version',
      },
      indirectObject: {
        reference: `urn:cps:bundle:${pathwayId}`,
        display: `Care-flow bundle for ${pathwayLabel}`,
        typeCode: 'care-flow-bundle',
      },
      action: 'C',
      outcome: '0',
    };
    const event = auditPayloadToFhir(payload);
    setLastPublished({ event, diff });
    onPublish?.(event, diff);
  };

  return (
    <section
      aria-labelledby="pathway-publish-flow-title"
      data-testid="pathway-publish-flow"
      data-pathway-id={pathwayId}
      data-publish-blocked={gate.ok ? 'false' : 'true'}
      className={[
        'flex flex-col gap-gutter-sm border-t border-surface-border bg-surface-muted p-gutter-md',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <header className="flex items-center justify-between">
        <h3
          id="pathway-publish-flow-title"
          className="text-signal-sm font-semibold uppercase tracking-wide text-text-tertiary"
        >
          Publish version
        </h3>
        <button
          type="button"
          data-testid="pathway-publish-flow-cta"
          aria-disabled={!gate.ok}
          disabled={!gate.ok}
          onClick={handlePublish}
          className="rounded-signal-md bg-signal-primary-500 px-gutter-md py-1 text-signal-sm font-semibold text-text-inverse focus:outline-none focus:ring-2 focus:ring-signal-primary-500 disabled:opacity-60"
        >
          Publish {pathwayLabel}
        </button>
      </header>

      {!gate.ok ? (
        <div
          role="alert"
          data-testid="pathway-publish-flow-gate-blocked"
          className="rounded-signal-md border border-signal-danger-500 bg-surface-base p-gutter-sm text-signal-sm text-signal-danger-500"
        >
          <p className="font-semibold">Publish blocked.</p>
          <p>{gate.details}</p>
          <ul className="mt-1 list-disc pl-gutter-md">
            {gate.reasons.map((r) => (
              <li
                key={r}
                data-testid={`pathway-publish-flow-gate-reason-${r}`}
              >
                {r}
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p
          role="status"
          data-testid="pathway-publish-flow-gate-ready"
          className="rounded-signal-md border border-signal-success-500 bg-surface-base p-gutter-sm text-signal-xs text-signal-success-500"
        >
          Gate ready. Sara {saraPassCount} of {saraTotalCount}, no open
          drafts, all cited components present.
        </p>
      )}

      <details
        data-testid="pathway-publish-flow-diff-details"
        className="rounded-signal-md border border-surface-border bg-surface-base p-gutter-sm"
      >
        <summary className="cursor-pointer text-signal-sm font-semibold text-text-primary">
          Diff preview ({diff.filter((e) => e.status !== 'unchanged').length}{' '}
          changed)
        </summary>
        <pre
          data-testid="pathway-publish-flow-diff-summary"
          className="mt-gutter-sm overflow-x-auto rounded-signal-sm bg-surface-muted p-gutter-sm font-mono text-signal-xs text-text-secondary"
        >
          {formatDiffSummary(diff)}
        </pre>
      </details>

      {lastPublished ? (
        <div
          data-testid="pathway-publish-flow-published"
          className="rounded-signal-md border border-signal-primary-500 bg-surface-base p-gutter-sm text-signal-sm text-text-primary"
        >
          <p className="font-semibold">
            Published. Simulated PR opened (Phase D will dispatch this to
            the backend git worker).
          </p>
          <p className="mt-1 font-mono text-signal-xs text-text-tertiary">
            AuditEvent source = {PUBLISH_AUDIT_SOURCE}, verb ={' '}
            {lastPublished.event.type.code}
          </p>
        </div>
      ) : null}
    </section>
  );
}
